import { supabase } from "./supabase";

class SalesService {
  // Get all companies
  async getCompanies(currentUserId) {
    try {
      // Step 1: Fetch user profile (role + assigned companies)
      const { data: profile, error: profileError } = await supabase
        .from("user_profiles")
        .select("role, user_companies")
        .eq("id", currentUserId)
        .single();

      if (profileError) {
        return { success: false, error: profileError.message };
      }

      const { role, user_companies } = profile;

      // console.log("role", role, "companies", user_companies);

      let companies = [];

      // Step 2: Fetch companies based on role
      if (role === "admin") {
        const { data, error } = await supabase
          .from("companies")
          .select("*")
          .order("name");

        if (error) {
          return { success: false, error: error.message };
        }

        companies = data;
        // console.log("compamy", companies);
      } else {
        if (!user_companies || user_companies.length === 0) {
          return { success: true, data: [] };
        }

        const { data, error } = await supabase
          .from("companies")
          .select("*")
          .in("id", user_companies)
          .order("name");

        if (error) {
          return { success: false, error: error.message };
        }

        companies = data;
        // console.log("compamy", companies);
      }

      return { success: true, data: companies };
    } catch (error) {
      if (
        error?.message?.includes("Failed to fetch") ||
        error?.message?.includes("NetworkError") ||
        (error?.name === "TypeError" && error?.message?.includes("fetch"))
      ) {
        return {
          success: false,
          error:
            "Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.",
        };
      }
      return { success: false, error: "Failed to load companies" };
    }
  }

  // Create new sales entry
  async createSalesEntry(entryData) {
    try {
      const { data, error } = await supabase
        .from("sales_entries")
        .insert([entryData])
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (
        error?.message?.includes("Failed to fetch") ||
        error?.message?.includes("NetworkError") ||
        (error?.name === "TypeError" && error?.message?.includes("fetch"))
      ) {
        return {
          success: false,
          error:
            "Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.",
        };
      }
      return { success: false, error: "Failed to create sales entry" };
    }
  }

  // Get sales entries for current user
  async getSalesEntries(filters = {}) {
    try {
      let query = supabase
        .from("sales_entries")
        .select(
          `
          *,
          companies (
            id,
            name,
            code
          )
        `
        )
        .order("entry_date", { ascending: false });

      // Apply filters
      if (filters?.startDate) {
        query = query.gte("entry_date", filters.startDate);
      }
      if (filters?.endDate) {
        query = query.lte("entry_date", filters.endDate);
      }
      if (filters?.companyId) {
        query = query.eq("company_id", filters.companyId);
      }
      if (filters?.status) {
        query = query.eq("status", filters.status);
      }

      const { data, error } = await query;

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (
        error?.message?.includes("Failed to fetch") ||
        error?.message?.includes("NetworkError") ||
        (error?.name === "TypeError" && error?.message?.includes("fetch"))
      ) {
        return {
          success: false,
          error:
            "Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.",
        };
      }
      return { success: false, error: "Failed to load sales entries" };
    }
  }

  // Update sales entry
  async updateSalesEntry(entryId, updates) {
    try {
      const { data, error } = await supabase
        .from("sales_entries")
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", entryId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (
        error?.message?.includes("Failed to fetch") ||
        error?.message?.includes("NetworkError") ||
        (error?.name === "TypeError" && error?.message?.includes("fetch"))
      ) {
        return {
          success: false,
          error:
            "Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.",
        };
      }
      return { success: false, error: "Failed to update sales entry" };
    }
  }

  // Delete sales entry
  async deleteSalesEntry(entryId) {
    try {
      const { error } = await supabase
        .from("sales_entries")
        .delete()
        .eq("id", entryId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      if (
        error?.message?.includes("Failed to fetch") ||
        error?.message?.includes("NetworkError") ||
        (error?.name === "TypeError" && error?.message?.includes("fetch"))
      ) {
        return {
          success: false,
          error:
            "Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.",
        };
      }
      return { success: false, error: "Failed to delete sales entry" };
    }
  }

  // Get analytics data (admin only)
  async getAnalyticsData(filters = {}) {
    try {
      let query = supabase.from("sales_entries").select(`
          *,
          companies (
            id,
            name,
            code
          ),
          user_profiles (
            id,
            full_name,
            email
          )
        `);

      // Apply filters
      if (filters?.startDate) {
        query = query.gte("entry_date", filters.startDate);
      }
      if (filters?.endDate) {
        query = query.lte("entry_date", filters.endDate);
      }

      const { data, error } = await query;

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (
        error?.message?.includes("Failed to fetch") ||
        error?.message?.includes("NetworkError") ||
        (error?.name === "TypeError" && error?.message?.includes("fetch"))
      ) {
        return {
          success: false,
          error:
            "Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.",
        };
      }
      return { success: false, error: "Failed to load analytics data" };
    }
  }

  // Real-time subscription to sales entries
  subscribeTo(table, callback, filters = {}) {
    const channel = supabase
      .channel(`${table}_changes`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: table,
          ...filters,
        },
        callback
      )
      .subscribe();

    return channel;
  }

  // Unsubscribe from real-time updates
  unsubscribe(channel) {
    if (channel) {
      supabase.removeChannel(channel);
    }
  }
}

const salesService = new SalesService();
export default salesService;
